import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import type { AdminState, DateRangePreset } from './types';
import type { PageViewsByDayData, PageViewsSummaryItem } from './api';

// Initialize date range to last 7 days
const endDate = endOfDay(new Date());
const startDate = startOfDay(subDays(endDate, 7));

const initialState: AdminState = {
  growthMetrics: {
    newUsers: 0,
    activatedUsers: 0,
    activatedUsersRate: 0,
    dau: 0,
    wau: 0,
    mau: 0,
    d7RetentionRate: 0,
    pathwaysCreated: 0,
    topicCompletions: 0,
    activeOrganizations: 0,
    inviteAcceptanceRate: 0
  },
  pageViewsByDay: [],
  totalPageViews: 0,
  pageViewsSummary: [],
  selectedPage: null,
  dateRange: [startDate, endDate],
  selectedDateRangePreset: '7',
  isGrowthLoading: false,
  growthError: false,
  isLoading: false,
  error: false
};

export const slice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    startGrowthLoading(state) {
      state.isGrowthLoading = true;
      state.growthError = false;
    },
    getGrowthMetricsSuccess(state, action: PayloadAction<AdminState['growthMetrics']>) {
      state.isGrowthLoading = false;
      state.growthMetrics = action.payload;
      state.growthError = false;
    },
    hasGrowthError(state, action: PayloadAction<boolean | string | null>) {
      state.isGrowthLoading = false;
      state.growthError = action.payload;
    },
    startLoading(state) {
      state.isLoading = true;
      state.error = false;
    },
    getPageViewsByDaySuccess(
      state,
      action: PayloadAction<{ pageViewsByDay: PageViewsByDayData[]; totalPageViews: number }>
    ) {
      state.isLoading = false;
      state.pageViewsByDay = action.payload.pageViewsByDay;
      state.totalPageViews = action.payload.totalPageViews;
      state.error = false;
    },
    getPageViewsSummarySuccess(state, action: PayloadAction<PageViewsSummaryItem[]>) {
      state.isLoading = false;
      state.pageViewsSummary = action.payload;
      // Auto-select top page if none selected
      if (!state.selectedPage && action.payload.length > 0) {
        state.selectedPage = action.payload[0].pageUrl;
      }
      state.error = false;
    },
    setSelectedPage(state, action: PayloadAction<string | null>) {
      state.selectedPage = action.payload;
    },
    setDateRange(state, action: PayloadAction<[Date | null, Date | null]>) {
      state.dateRange = action.payload;
    },
    setSelectedDateRangePreset(state, action: PayloadAction<DateRangePreset>) {
      state.selectedDateRangePreset = action.payload;
    },
    hasError(state, action: PayloadAction<boolean | string | null>) {
      state.isLoading = false;
      state.error = action.payload;
    }
  }
});

export default slice.reducer;
